// src/context/AuthContext.js - Complete Authentication Context with Firebase Persistence
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithRedirect,        
  getRedirectResult,  
  updateProfile,
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { apiService } from '../services/api';
import { getReferralDataForSignup, clearStoredReferral } from '../utils/referralUtils';
import { trackAuthEvent, trackEvent, setUserAdminStatus } from '../utils/analytics';
import { trackRedditSignup, updateRedditUserMatch } from '../utils/redditPixel'; 

const tokenCache = {
  lastCheck: null,
  cachedToken: null,
  cachedProfile: null,
  needsCheck: function(currentToken) {
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;
    return !this.lastCheck || this.cachedToken !== currentToken || (now - this.lastCheck) > fiveMinutes;
  },
  save: function(token, profile) {
    this.lastCheck = Date.now();
    this.cachedToken = token;
    this.cachedProfile = profile;
  },
  get: function() {
    return this.cachedProfile;
  },
  clear: function() {
    this.lastCheck = null;
    this.cachedToken = null;
    this.cachedProfile = null;
  }
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Clear error function
  const clearError = () => setError('');

  // Check if user is admin
  const isAdmin = userProfile?.role === 'ADMIN' || userProfile?.role === 'admin';

 // In AuthContext.js - Replace the onAuthStateChanged useEffect with this:

useEffect(() => {
  const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
    try {
      clearError();

      if (firebaseUser) {
  console.log('✅ Firebase user detected:', firebaseUser.email);

  // Reload to pick up fresh verification status (e.g. the user clicked the
  // verification link in another tab/device). Mirrors the native app.
  if (!firebaseUser.emailVerified) {
    try { await firebaseUser.reload(); } catch (e) { /* ignore reload errors */ }
  }
  const liveUser = auth.currentUser || firebaseUser;

  // Still unverified — hold on the auth screen and never load a profile.
  if (!liveUser.emailVerified) {
    console.log('⚠️ Unverified user - holding on auth screen, no profile loaded');
    setUser(liveUser);
    setUserProfile(null);
    tokenCache.clear();
    return;
  }

  // Set the Firebase user immediately
  setUser(liveUser);

  try {
    // 🔹 NEW: Get the current token
    const currentToken = await liveUser.getIdToken();
    
    // 🔹 NEW: Check if we need to verify (or use cache)
    if (tokenCache.needsCheck(currentToken)) {
      console.log('🔄 Checking token (cache expired or new token)');
      
      // Get user profile from backend
      const response = await apiService.auth.verifyToken();
      
      if (response.data.success) {
        const profileData = response.data.data.user;
        setUserProfile(profileData);
        setUserAdminStatus(profileData.role === 'ADMIN' || profileData.role === 'admin');
        
        // 🔹 NEW: Save to cache
        tokenCache.save(currentToken, profileData);
        console.log('✅ Profile loaded and cached for 5 minutes');
        
        // Auto-sync verification status if there's a mismatch
        if (liveUser.emailVerified !== profileData.is_verified) {
          console.log('⚠️ Verification status mismatch detected - forcing sync...');
          const syncedProfile = await syncVerificationStatus();
          if (syncedProfile) {
            setUserProfile(syncedProfile);
            tokenCache.save(currentToken, syncedProfile); // Update cache
          }
        }
        
        trackEvent('user_authenticated', 'Authentication', profileData.role);
      }
    } else {
      // 🔹 NEW: Use cached profile (no API call needed!)
      console.log('✅ Using cached profile (checked recently)');
      const cachedProfile = tokenCache.get();
      if (cachedProfile) {
        setUserProfile(cachedProfile);
        setUserAdminStatus(cachedProfile.role === 'ADMIN' || cachedProfile.role === 'admin');
      }
    }
  } catch (error) {
    console.error('❌ Profile fetch error:', error);
    
    // Handle 404 specifically
    if (error.response?.status === 404) {
      console.log('New user - profile not found in database (will be handled by sign-in flow)');
      setUserProfile(null);
      tokenCache.clear(); // 🔹 NEW: Clear cache on error
    } else if (error.response?.status === 401) {
      console.log('Invalid token - signing out');
      await signOut(auth);
      setUserProfile(null);
      tokenCache.clear(); // 🔹 NEW: Clear cache on error
    } else {
      // Other errors - keep user signed in but clear profile
      setUserProfile(null);
      
    }
  }

      } else {
        console.log(' No Firebase user - signed out');
        setUser(null);
        setUserProfile(null);
        setUserAdminStatus(false);
        tokenCache.clear();
      }
    } catch (error) {
      console.error('Auth state change error:', error);
      setUser(null);
      setUserProfile(null);
      setUserAdminStatus(false);
    } finally {
      // CRITICAL: Always set loading to false, even on errors
      setLoading(false);
    }
  });

  return unsubscribe;
}, []);




  const syncVerificationStatus = async () => {
  if (!user) return null;
  
  try {
  //  console.log('ðŸ”„ Syncing verification status with backend...');
    const response = await apiService.auth.syncVerification();
    
    if (response.data.success) {
      const updatedProfile = response.data.data.user;
      setUserProfile(updatedProfile);
      
   //   console.log('âœ… Verification status synced:', updatedProfile.is_verified);
      return updatedProfile;
    }
  } catch (error) {
    console.error('âŒ Verification sync error:', error);
    return null;
  }
};
  // Sign up function
   const signup = async (email, password, username = '', gender = '',consents = {}) => {
    try {
      //setLoading(true);
      clearError();

      trackAuthEvent('register_attempt', 'email');
      
    //  console.log('ðŸ“ Creating account for:', email);

     //     console.log('ðŸŒ Checking user location...');
    try {
      await apiService.auth.checkLocation(email);
     // console.log('âœ… Location check passed - UK user');
    } catch (locationError) {
      console.error('âŒ Location check failed:', locationError);
      
      // Get the error message
      const locationErrorMsg = locationError.response?.data?.detail || 
                              'Registration is currently unavailable in your region';
      
      trackAuthEvent('register_blocked', 'location');
      setError(locationErrorMsg);
      
      // Throw it so Login.js can catch and display
      throw new Error(locationErrorMsg);
    }
      
      // Create user with Firebase
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

    //  console.log('ðŸ“§ Sending email verification...');
      await sendEmailVerification(firebaseUser);
      
      // Update display name if username provided
      if (username) {
        await updateProfile(firebaseUser, { displayName: username });
      }

      const referralData = getReferralDataForSignup();
      
      // Register with backend
      const userData = {
        email,
        username: username || email.split('@')[0],
        gender: gender,
        marketing_consent: consents.marketing_consent || false,
        terms_accepted: consents.terms_accepted || true,
        marketing_consent_at: consents.marketing_consent_at,
        terms_accepted_at: consents.terms_accepted_at,
        ...(referralData && referralData)
      };
      
    //  console.log('ðŸ“¤ Registering with backend:', userData);
      await apiService.auth.register(userData);

      if (referralData) {
        clearStoredReferral();
    //    console.log('âœ… Referral tracked and cleared');
      }


      trackAuthEvent('register_success', 'email');

          // ADD GOOGLE ADS CONVERSION TRACKING HERE
    if (window.gtag) {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-17519858723/yOUSCIu3yKkbEKOoj6JB',
        'value': 1.0,
        'currency': 'GBP'
      });
    //  console.log('âœ… Google Ads conversion tracked');
    }
    // END CONVERSION TRACKING

    trackRedditSignup({
  value: 1.0,
  currency: 'GBP',
  transactionId: firebaseUser.uid
    });
      
     // console.log('âœ… Account created successfully - verification email sent');
      return { 
        success: true, 
        message: 'Account created successfully! Please check your email and verify your account before signing in.',
        requiresVerification: true 
      };
      
    } catch (error) {
      console.error('âŒ Signup error:', error);

      trackAuthEvent('register_failure', 'email');
      trackEvent('register_error', 'Authentication', error.code);

      const errorMessage = error.code === 'auth/email-already-in-use'
        ? 'An account with this email already exists'
        // /register is now also an authoritative UK-only gate (not just the
        // /check-location pre-check above) - if it ever blocks here (e.g. the
        // detected location changed between the two calls), surface its
        // actual detail message instead of a generic axios error string.
        : error.response?.data?.detail || error.message || 'Failed to create account';
      setError(errorMessage);
      throw error;
    } 
/*     finally {
      setLoading(false);
    } */
  };

  // Login function  
const login = async (email, password) => {
  try {
    setLoading(true);
    clearError();

    trackAuthEvent('login_attempt', 'email');
    
  //  console.log('ðŸ”‘ Signing in:', email);
    
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    let firebaseUser = userCredential.user;

    // If the freshly signed-in user still looks unverified, reload once in case they
    // clicked the verification link elsewhere and the local record is stale.
    const reloaded = !firebaseUser.emailVerified;
    if (reloaded) {
      await firebaseUser.reload();
      firebaseUser = auth.currentUser;
    }

    // ðŸ”§ ENHANCED: More detailed verification checking
   // console.log('ðŸ“§ Email verification status:', firebaseUser.emailVerified);
    
    // Check if email is verified
    if (!firebaseUser?.emailVerified) {
     // console.log('âŒ Email not verified - signing out');
      setError('Please verify your email before signing in. Check your email inbox and click the verification link.');
      await signOut(auth);
      
      trackAuthEvent('login_failure', 'email_not_verified');
      
      return {
        success: false,
        message: 'Please verify your email before signing in. Check your email inbox and click the verification link.',
        requiresVerification: true
      };
    }

    // Force a token refresh so the persisted token carries email_verified=true.
    if (reloaded) {
      await firebaseUser.getIdToken(true);
    }

    trackAuthEvent('login_success', 'email');
  //  console.log('âœ… Login successful - email verified');
    
    // The onAuthStateChanged listener will handle syncing verification status
    return {
      success: true,
      message: 'Signed in successfully!',
      user: firebaseUser
    };
    
  } catch (error) {
    console.error('âŒ Login error:', error);

    trackAuthEvent('login_failure', 'email');
    trackEvent('login_error', 'Authentication', error.code);

    const errorMessage = error.code === 'auth/user-not-found' 
      ? 'No account found with this email'
      : error.code === 'auth/wrong-password'
      ? 'Incorrect password'
      : error.code === 'auth/invalid-email'
      ? 'Invalid email address'
      : error.code === 'auth/user-disabled'
      ? 'This account has been disabled'
      : error.code === 'auth/invalid-credential'
      ? 'Invalid email or password'
      : error.message || 'Failed to sign in';
    setError(errorMessage);
    throw error;
  } finally {
    setLoading(false);
  }
};
const resendVerificationEmail = async () => {
  if (!user) {
    throw new Error('No user signed in');
  }
  
  try {
    setLoading(true);
   // console.log('ðŸ“§ Resending verification email...');
    
    // Send verification email through Firebase
    await sendEmailVerification(user);
    
    // Also call backend endpoint if user is signed in
    try {
      await apiService.auth.resendVerification();
    //  console.log('âœ… Backend verification email request sent');
    } catch (backendError) {
      console.warn('âš ï¸ Backend verification request failed:', backendError);
      // Don't fail the whole operation if backend call fails
    }
    
    trackAuthEvent('verification_resent', 'email');
    
    return {
      success: true,
      message: 'Verification email sent! Please check your inbox.'
    };
  } catch (error) {
    console.error('âŒ Resend verification error:', error);
    trackAuthEvent('verification_resend_failure', 'email');
    throw error;
  } finally {
    setLoading(false);
  }
};
  // Google Sign In function
// In AuthContext.js - Replace your signInWithGoogle function:

// In AuthContext.js - Replace your signInWithGoogle function with this:

const signInWithGoogle = async () => {
  try {
    setError('');
    setLoading(true);

    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const firebaseUser = result.user;
    
    console.log('Google sign-in successful, checking if user exists...');
    
    // Check if this user already exists in our database
    try {
      const response = await apiService.auth.verifyToken();
      
      if (response.data.success) {
        // Existing user - all good!
        console.log('âœ… Existing user found in database');
        trackAuthEvent('login_success', 'google');
        
        return {
          success: true,
          message: 'Signed in with Google successfully!',
          user: firebaseUser,
          isNewUser: false
        };
      }
    } catch (error) {
      // User doesn't exist in our database yet
      if (error.response?.status === 404) {
        console.log('New Google user - needs to complete registration');
        
        // Return with flag indicating we need more info
        return {
          success: false,
          requiresAdditionalInfo: true,
          user: firebaseUser,
          isNewUser: true,
          message: 'Please complete your profile to continue'
        };
      }
      
      // Some other error
      console.error('Unexpected error during Google sign-in:', error);
      throw error;
    }

  } catch (error) {
    console.error('Google sign-in error:', error);
    
    // Sign out the user from Firebase since we can't proceed
    await signOut(auth);
    
    const errorMessage = getFirebaseErrorMessage(error.code) || error.message || 'Google sign-in failed';
    setError(errorMessage);
    throw new Error(errorMessage);
  } finally {
    setLoading(false);
  }
};

// Add a new function to complete Google registration
const completeGoogleRegistration = async (firebaseUser, gender, consents) => {
  try {
    if (!firebaseUser) {
      throw new Error('No user signed in');
    }

    console.log('ðŸ“ Completing Google user registration for:', firebaseUser.email);
    
    const referralData = getReferralDataForSignup();
    
    const userData = {
      email: firebaseUser.email,
      username: firebaseUser.displayName || firebaseUser.email.split('@')[0],
      gender: gender,
      marketing_consent: consents.marketingConsent || false,
      terms_accepted: true,
      terms_accepted_at: new Date().toISOString(),
      marketing_consent_at: consents.marketingConsent ? new Date().toISOString() : null,
      ...(referralData && referralData)
    };
    
    await apiService.auth.register(userData);
    
    if (referralData) {
      clearStoredReferral();
    }
    
    // Refresh the profile
    await refreshUserProfile();
    
    trackAuthEvent('register_success', 'google');

        // GOOGLE ADS CONVERSION TRACKING 
    if (window.gtag) {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-17519858723/yOUSCIu3yKkbEKOoj6JB',
        'value': 1.0,
        'currency': 'GBP'
      });
    }

        trackRedditSignup({
      value: 1.0,
      currency: 'GBP',
      transactionId: firebaseUser.uid
    });
    // END CONVERSION TRACKING
    
    return {
      success: true,
      message: 'Registration completed successfully!'
    };
    
  } catch (error) {
    console.error('âŒ Google registration completion error:', error);
    
    // Sign out on error
    await signOut(auth);
    throw error;
  }
};
  // Logout function
  const logout = async () => {
    try {
  //    console.log('ðŸšª Signing out...');

      trackAuthEvent('logout', 'manual');

      await signOut(auth);
      
      // Clear local state immediately
      setUser(null);
      setUserProfile(null);
      clearError();
      
  //    console.log('âœ… Signed out successfully');
    } catch (error) {
      console.error('âŒ Logout error:', error);

      trackEvent('logout_error', 'Authentication', error.code);

      setError('Failed to sign out');
    }
  };

  // Reset password function
  const resetPassword = async (email) => {
    try {
 //     console.log('ðŸ“§ Sending password reset email to:', email);

      trackAuthEvent('password_reset_attempt', 'email');

      await sendPasswordResetEmail(auth, email);

      trackAuthEvent('password_reset_success', 'email');

 //     console.log('âœ… Password reset email sent');
      return { success: true, message: 'Password reset email sent' };
    } catch (error) {
      console.error('âŒ Password reset error:', error);

      trackAuthEvent('password_reset_failure', 'email');

      const errorMessage = error.code === 'auth/user-not-found'
        ? 'No account found with this email'
        : error.message || 'Failed to send password reset email';
      throw new Error(errorMessage);
    }
  };

  // Refresh user profile function
const refreshUserProfile = async () => {
  try {
    if (!user) return null;
    
    const currentToken = await user.getIdToken();
    
    // 🔹 Check cache first
    if (!tokenCache.needsCheck(currentToken)) {
      console.log('✅ Using cached profile for refresh');
      return tokenCache.get();
    }
    
    console.log('🔄 Refreshing user profile...');
    const response = await apiService.auth.verifyToken();
    
    if (response.data.success) {
      const profileData = response.data.data.user;
      setUserProfile(profileData);
      tokenCache.save(currentToken, profileData); // 🔹 Save to cache
      trackEvent('profile_loaded', 'User', profileData.role);
      console.log('✅ User profile refreshed and cached');
      return profileData;
    }
  } catch (error) {
    console.error('❌ Profile refresh error:', error);
  }
};

  // Update user profile function
  const updateUserProfile = async (updateData) => {
    try {
  //    console.log('ðŸ“ Updating user profile:', updateData);
      const response = await apiService.auth.updateProfile(updateData);
      
      if (response.data.success) {
        const updatedProfile = response.data.data.user;
        setUserProfile(updatedProfile);
        tokenCache.clear()
   //     console.log('âœ… Profile updated successfully');
        return updatedProfile;
      }
    } catch (error) {
      console.error('âŒ Profile update error:', error);
      throw error;
    }
  };
// Helper function to get user-friendly error messages
const getFirebaseErrorMessage = (errorCode) => {
  switch (errorCode) {
    case 'auth/user-not-found':
      return 'No account found with this email address.';
    case 'auth/wrong-password':
      return 'Incorrect password.';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters.';
    case 'auth/invalid-email':
      return 'Invalid email address.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection.';
    case 'auth/popup-closed-by-user':
      return 'Sign-in popup was closed. Please try again.';
    case 'auth/popup-blocked':
      return 'Sign-in popup was blocked. Please allow popups and try again.';
    case 'auth/cancelled-popup-request':
      return 'Sign-in was cancelled. Please try again.';
    case 'auth/account-exists-with-different-credential':
      return 'An account already exists with this email using a different sign-in method.';
    default:
      return 'An error occurred. Please try again.';
  }
};
  // ... your existing value object
  const value = {
    // User state
    user,
    userProfile,
    loading,
    error,
    isAdmin,
    
    // Auth functions
    signup,
    login,
    logout,
    resetPassword,

    
    // Utility functions
    clearError,
    refreshUserProfile,
    updateUserProfile,
    signInWithGoogle, 
    completeGoogleRegistration,
    syncVerificationStatus,      // Add this
  resendVerificationEmail  
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};